import {DataQueryCategory} from './data-query-category.model';
import {DataQueryField} from './data-query-field.model';
import {DataQueryObject} from './data-query-object.model';
import {DataQuery} from './data-query.model';
import {System} from '../../shared-components/System';
import {NestedData} from '../../shared-components/virtualized-table/nested-data';
import {FieldAutofillOption} from './field-search/field-autofill-option';
export class DataQueryInformation {

    // Static Properties
    static virtualizedTableColumns = {
        'field': ['data'],
        'category': ['more', 'data'],
        'object': ['more', 'data']
    };

    // Properties
    categories: DataQueryCategory[] = [];
    fields: DataQueryField[] = [];
    objects: DataQueryObject[] = [];
    recentQueries: DataQuery[] = [];
    recentStudentFilters: DataQuery[] = [];
    recentCourseFilters: DataQuery[] = [];
    recentStaffFilters: DataQuery[] = [];
    userCategories: number[] = [];
    visibleInformation: DataQueryInformation;
    defaultQuery: DataQuery;
    organized = false;
    initialized = false;

    // Class Functions
    get visibleObjects() {
        // PRE: the information is organized
        // POST: returns the visible objects
        return this.objects.filter(object =>
            !!object.categories.find(category =>
                !!category.fields.find(field => field.Visible)
            )
        );
    }

    // Methods
    getField(searchFunction: (value: DataQueryField) => boolean) {
        // POST: returns a duplicate of the field specified by the search function
        const field = this.fields.find(searchFunction);
        if (field) {
            return field.duplicate();
        } else {
            return null;
        }
    }

    organize() {
        // PRE: the categories, fields, and objects are set, and the preconditions of each organize function
        // POST: aligns the data such that each one references the others
        this.organized = true;
        this.organizeObjects();
        this.organizeCategories();
        this.organizeFilters();
        this.initialized = true;
    }

    organizeObjects() {
        // PRE: if an object has categories, ALL OBJECTS HAVE CATEGORIES
        // POST: organizes the objects with the categories
        let resetCategories = false;
        this.objects.forEach(object => {

            // If it is not initialized, it needs to go through the process of setting the downward references
            if (!this.initialized) {

                // If it already has categories, treat those as authoritative
                if (object.categories && object.categories.length > 0) {

                    // Removing old categories because we no longer need them
                    if (!resetCategories) {
                        this.categories = [];
                        resetCategories = true;
                    }

                    // Setting new categories
                    for (let i = 0; i < object.categories.length; i++) {
                        object.categories[i] = System.convert<DataQueryCategory>(object.categories[i], new DataQueryCategory());
                        object.categories[i].object = object;
                        if (!object.categories[i].ObjectName) {
                            object.categories[i].ObjectName = object.ObjectName;
                        }
                        this.categories.push(object.categories[i]);
                    }
                }

                // If it does not have categories, compose new ones
                else {
                    object.categories = [];
                    this.categories.forEach(category => {
                        if (category.ObjectID == object.ID) {
                            object.categories.push(category);
                            category.object = object;
                            if (!category.ObjectName) {
                                category.ObjectName = object.ObjectName;
                            }
                        }
                    });
                }
            }

            // If it is initialized, it just needs to reset the upward references
            else {
                object.categories.forEach(category => {
                    category.object = object;
                });
            }
        });
    }

    organizeCategories() {
        // PRE: if an category has fields, ALL CATEGORIES HAVE FIELDS
        // POST: organizes the categories with the fields
        let resetFields = false;
        this.categories.forEach(category => {

            // If it is not initialized, it needs to compose the downward references
            if (!this.initialized) {

                // If it has fields, treat those as authoritative
                if (category.fields && category.fields.length > 0) {

                    // Removing fields because we no longer need them
                    if (!resetFields) {
                        this.fields = [];
                        resetFields = true;
                    }

                    // Setting the new fields
                    for (let i = 0; i < category.fields.length; i++) {
                        category.fields[i] = System.convert<DataQueryField>(category.fields[i], new DataQueryField());
                        category.fields[i].category = category;
                        category.fields[i].object = category.object;
                        if (!category.fields[i].CategoryName) {
                            category.fields[i].CategoryName = category.CategoryName;
                        }
                        if (!category.fields[i].ObjectName) {
                            category.fields[i].ObjectName = category.ObjectName;
                        }
                        this.fields.push(category.fields[i]);
                    }
                }

                // If it does not have fields, compose new ones
                else {
                    category.fields = [];
                    category.isUserCategory = this.userCategories.length == 0 || this.userCategories.includes(category.ID);
                    this.fields.forEach(field => {
                        if (field.CategoryID == category.ID   && field.UserDefinedTabId == category.UserDefinedTabId) {
                            category.fields.push(field);
                            field.category = category;
                            field.object = category.object;
                            if (!field.CategoryName) {
                                field.CategoryName = category.CategoryName;
                            }
                            if (!field.ObjectName) {
                                field.ObjectName = category.ObjectName;
                            }
                        }
                    });
                }
            }

            // If it is initialized, it just needs to set the upward references
            else {
                category.fields.forEach(field => {
                    field.category = category;
                    field.object = category.object;
                });
            }
        });
    }

    organizeFilters() {
        // POST: organizes the filters with the fields
        this.fields.forEach(field => {
            field.Filters = [];
            if (field.Filters) {
                field.Filters.forEach(filter => {
                    filter.field = field;
                });
            }
        });
    }

    disorganize() {
        // POST: resets data to its unaligned state
        // NOTE: maintains a downward hierarchy
        this.organized = false;

        // Disorganizing objects
        this.objects.forEach(object => {
            // delete object.categories;
        });

        // Disorganizing categories
        this.categories.forEach(category => {
            // delete category.fields;
            delete category.object;
        });

        // Disorganizing fields
        this.fields.forEach(field => {
            delete field.object;
            delete field.category;
            if (field.Filters) {
                field.Filters.forEach(filter => {
                    delete filter.field;
                });
            }
        });
    }

    getVisible (V10, searchString = '', changed = false): DataQueryInformation {
        // PRE: V10. Optionally, a search string and whether or not the data has been changed
        // POST: returns this data structure panelFiltered for all of the non-visible fields

        // Returning the visible information if it's there
        if (this.visibleInformation && !searchString && !changed) {
            return this.visibleInformation;
        }

        // Initializing visible information
        this.visibleInformation = new DataQueryInformation();
        Object.assign(this.visibleInformation, this);

        // Removing categories
        this.categories.forEach(category => {
            category.isUserCategory = this.userCategories.length == 0 || this.userCategories.includes(category.ID);
        });
        this.visibleInformation.categories = this.visibleInformation.categories.filter(
            function(category) {
                return category.fields.length > 0 && !category.Deleted && category.isUserCategory;
            }
        );

        // Removing fields
        this.visibleInformation.fields = this.visibleInformation.fields.filter(
            function(field) {
                return field.Visible &&
                    !field.Deleted &&
                    field.DisplayName &&
                    (!searchString || field.DisplayName.toLowerCase().indexOf(searchString.toLowerCase()) >= 0) &&
                    (!V10 || (field.DisplayName != '(Students Actively Enrolled Today in) Class' && V10));
            }
        );

        // Removing objects
        this.visibleInformation.objects = this.visibleInformation.objects.filter(
            function(object) {
                return object.categories.length > 0 && !object.Deleted;
            }
        );

        // Returning information
        return this.visibleInformation;
    }

    virtualizedTableData(
        fieldSearch = '',
        V10 = false,
        allowTab = false,
        click = (event: MouseEvent, nestedData: NestedData) => {},
        keydown = (event: KeyboardEvent, nestedData: NestedData) => {},
        filterColumnSQL: string[] = null,
        filterColumnFieldName: string[] = null,
        disabledFields: { [fieldId: string]: boolean } = null
    ): NestedData[] {
        // PRE: the information is organized
        // POST: returns the nested data for a virtualized table
        const newTableData = [];

        // Obtaining our partial searches
        // If we have two or more, apply it as object.category.field
        const searchComponents = fieldSearch.split('.');
        const partialSearches = searchComponents.length > 1;

        // Filtering data
        this.objects.forEach(object => {

            // Composing nested data objects
            const objectRoot = new NestedData(object, null, object.ObjectName + object.ID, 'object');
            const objectNameMatch = object.ObjectName.toLowerCase().includes(searchComponents[0].toLowerCase());
            objectRoot.style = {
                'background': objectNameMatch && fieldSearch ? 'lightyellow' : 'inherit'
            };
            if (!partialSearches || objectNameMatch) {
                object.categories.forEach(category => {

                    // Creating the categories
                    const categoryRoot = new NestedData(category, objectRoot, category.CategoryName + category.ID, 'category');
                    const categoryNameMatch = searchComponents.length >= 2 ? category.CategoryName.toLowerCase().includes(searchComponents[1].toLowerCase()) : false;
                    categoryRoot.style = {
                        'background': categoryNameMatch && fieldSearch ? 'lightyellow' : 'inherit'
                    };
                    if (
                        (
                            !partialSearches
                            || (searchComponents.length < 2 || categoryNameMatch)
                        )
                        && (
                            !filterColumnSQL
                            || filterColumnSQL.length == 0
                            || category.Fields.find(f => filterColumnSQL.map(o => o.toLowerCase()).includes(f.SQLColumnName.toLowerCase()))
                        )
                        && (
                            !filterColumnFieldName
                            || filterColumnFieldName.length == 0
                            ||  category.Fields.find(f => filterColumnFieldName.map(o => o.toLowerCase()).includes(f.FieldName.toLowerCase()))
                        )
                    ) {
                        category.fields.forEach(field => {

                            // Creating fields and adding them, while also filtering for our search
                            // NOTE: we add if either the object matches OR the field matches when no . are present
                            let fieldNameMatch = false;
                            if (partialSearches) {
                                if (searchComponents.length >= 3) {
                                    fieldNameMatch = field.DisplayName.toLowerCase().includes(searchComponents[2].toLowerCase());
                                } else {
                                    fieldNameMatch = false;
                                }
                            } else {
                                fieldNameMatch = field.DisplayName.toLowerCase().includes(fieldSearch.toLowerCase());
                            }
                            if ((
                                    (partialSearches && (searchComponents.length < 3 || fieldNameMatch)) ||
                                    (!partialSearches && (!fieldSearch || objectNameMatch || fieldNameMatch))
                                ) &&
                                (!V10 || field.DisplayName != '(Students Actively Enrolled Today in) Class') &&
                                (disabledFields ? !disabledFields[field.ObjectName + '-' + field.CategoryID + '-' + field.ID] : true) &&
                                field.Visible
                            ) {
                                const fieldRoot = new NestedData(field, categoryRoot, field.DisplayName + field.ID, 'field');
                                fieldRoot.style = {
                                    'background': fieldNameMatch && fieldSearch ? 'lightyellow' : 'inherit'
                                };
                                fieldRoot.allowTab = allowTab;
                                fieldRoot.ariaLabel = object.ObjectName + ', ' + category.CategoryName + ', ' + field.DisplayName;
                                fieldRoot.click = click;
                                fieldRoot.keydown = keydown;
                                categoryRoot.children.push(fieldRoot);
                            }
                        });

                        // Pushing the category if it is not empty
                        if (categoryRoot.children.length != 0) {
                            objectRoot.children.push(categoryRoot);
                        }
                    }
                });

                // Pushing the object to the table data if it is not empty or there is a name match for partial searches
                if (objectRoot.children.length != 0) {
                    newTableData.push(objectRoot);
                }
            }
        });

        // Resetting the reference to trigger changes
        return newTableData;
    }

    getAutofillOptions(fieldSearch: string, V10 = false): FieldAutofillOption[] {
        // PRE: the search field for autofill
        // POST: obtains a list of autofill options for the field search
        const autofills: FieldAutofillOption[] = [];

        // Obtaining our partial searches
        // If we have two or more, apply it as object.category.field
        const searchComponents = fieldSearch.split('.');

        // Don't do any autofilling if there is a period without a letter behind it
        // This prevents some weirdness with '.' and '..'
        for (let i = 0; i < searchComponents.length; i++) {
            if (searchComponents[i] == '' && (i == 0 || searchComponents[i - 1] == '')) {
                return [];
            }
        }

        // Obtaining qualifying objects
        this.objects.forEach(object => {
            if (object.ObjectName.toLowerCase().includes(searchComponents[0].toLowerCase())) {

                // Pushing object autofills if the length is equal to 1
                if (searchComponents.length == 1) {
                    const objectAutofill = new FieldAutofillOption();
                    objectAutofill.displayString = object.ObjectName;
                    objectAutofill.applyString = object.ObjectName;
                    autofills.push(objectAutofill);
                }

                // Obtaining qualifying categories
                else {
                    object.categories.forEach(category => {
                        if (searchComponents.length >= 2 && category.CategoryName.toLowerCase().includes(searchComponents[1].toLowerCase())) {

                            // Pushing category autofills if the length is equal to 2
                            if (searchComponents.length == 2) {
                                const categoryAutofill = new FieldAutofillOption();
                                categoryAutofill.displayString = category.CategoryName;
                                categoryAutofill.applyString = object.ObjectName + '.' + category.CategoryName;
                                autofills.push(categoryAutofill);
                            }

                            // Obtaining qualifying fields if the length is equal to 3
                            else {
                                category.fields.forEach(field => {

                                    // Composing autofills for qualifying fields
                                    if (field.DisplayName.toLowerCase().includes(searchComponents[2].toLowerCase()) &&
                                        (!V10 || field.DisplayName != '(Students Actively Enrolled Today in) Class') &&
                                        field.Visible) {
                                        const fieldAutofill = new FieldAutofillOption();
                                        fieldAutofill.displayString = field.DisplayName;
                                        fieldAutofill.applyString = object.ObjectName + '.' + category.CategoryName + '.' + field.DisplayName;
                                        autofills.push(fieldAutofill);
                                    }
                                });
                            }
                        }
                    });
                }
            }
        });

        // Returning the discovered autofill
        return autofills;
    }
}
